import { useEffect, useMemo, useState } from 'react';
import {
  useSandboxSessionsStream,
  type SandboxSessionDTO,
} from '@/hooks/use-sandbox-sessions-stream';
import { Check, Copy, Loader2 } from 'lucide-react';
import { QRCode } from 'react-qr-code';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFetch } from '@/hooks/use-fetch';
import { useFacebookEnabled, useInstagramEnabled } from '@/hooks/use-app-config';
import { useBindCode } from '@/pages/sandbox/queries';
import { formatSandboxPhone } from '@/lib/format-sandbox-phone';
import { clearHandoffRedirectPending } from '@/lib/handoff-storage';
import { useWorkspace } from '@/contexts/workspace-context';
import { emit } from '@/lib/analytics';
import { BootstrapCodeDialog } from './bootstrap-code-dialog';
// Phase 129 Plan 06 — demo-handoff consent banner + change-flow escape hatch.
import { DemoHandoffBanner } from './quick-start/demo-handoff-banner';
import { useChangeFlow } from './quick-start/use-change-flow';
import { PageContainer } from '@/components/page-container';

/**
 * Phase 127 Plan 01: Post-signup Quick Start onboarding page.
 *
 * A persistent two-step wizard that composes the Phase 126 sandbox bind
 * surface (Step 1) with the Phase 122 BootstrapCodeDialog (Step 2), with
 * two variants:
 *
 *   Case A — zero active sandbox sessions: render bind surface + disabled
 *   Step 2. On focus refetch, if a new active session appears, Step 1
 *   collapses to a ✓ bound-to-phone summary and Step 2 becomes enabled.
 *
 *   Case B — >=1 active session: render a Select picker preselecting the
 *   most-recently-bound session, with Step 2 immediately enabled. A "Bind
 *   A New Number Instead" action swaps Step 1 to the Case A bind surface
 *   with a "← Back to picker" affordance.
 *
 * Decisions diverging from UI-SPEC (see 127-01-PLAN.md <decisions>):
 *   D1. Bound-session summary renders `selectedSession.phone` RAW — the
 *       formatSandboxPhone helper is designed for the 12-digit env-var
 *       shape ('972557046276'); passing a '+'-prefixed session.phone
 *       double-prefixes ('++15551234567'). Matches sandbox.tsx:551
 *       precedent. Picker rows likewise render session.phone raw.
 *   D2. Playwright navigate uses waitUntil: 'domcontentloaded' — see
 *       actions.ts for the rationale (catch-all fallback keeps network
 *       busy during RED phase).
 *
 * Phase 125-06 PostHog instrumentation:
 *   5 named events fire from this page via `emit()` — quickstart_code_copied,
 *   quickstart_phone_copied, quickstart_whatsapp_link_opened,
 *   quickstart_session_selected + sandbox_instructions_copied (via the
 *   nested BootstrapCodeDialog). Emits fail open (analytics.ts swallows
 *   posthog exceptions) so telemetry never blocks the funnel UX.
 */

interface SandboxSession {
  id: string;
  // Phase 129 Plan 06 — test fixtures distinguish raw `id` from `publicId`
  // (`ssn_*`). In production responses these are the same value (the backend
  // strips the raw UUID in `toPublicSession` and exposes the publicId as
  // `id`), so this is optional. The change-flow DELETE prefers `publicId`
  // when present.
  publicId?: string;
  workspaceId: string;
  createdByUserId: string;
  phone: string | null;
  accessToken: string;
  status: string;
  webhookUrl: string | null;
  cloudflareTunnelId: string | null;
  hostname: string | null;
  hmacSecret: string;
  activatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  // Phase 129 Plan 05 — `origin` distinguishes manually-bound sessions
  // (`'manual'`, the default) from sessions auto-created by the homepage demo
  // → signup → workspace handoff (`'demo_handoff'`) and from the anonymous
  // browser-demo seed row (`'demo_anonymous'`). Plan 06 reads this on the
  // page to decide whether to render the consent banner.
  origin?: 'manual' | 'demo_handoff' | 'demo_anonymous';
  // STI discriminator; defaults to 'whatsapp' when absent (legacy rows).
  type?: 'whatsapp' | 'instagram' | 'facebook';
  /** facebook-only (AIT-621) — display name of the bound Messenger sender. */
  facebookSenderName?: string | null;
  senderInstagramId?: string | null;
  accountInstagramId?: string | null;
  /** IG-only — customer handle resolved at bind time. NULL while the
   *  async resolver hasn't populated yet; UI falls back to IGSID. */
  senderInstagramUsername?: string | null;
}

const SANDBOX_NUMBER = import.meta.env.VITE_SANDBOX_PHONE_NUMBER ?? '';
const SANDBOX_IG_USERNAME =
  import.meta.env.VITE_SANDBOX_IG_USERNAME ?? 'hookmyappsandboxstaging';
// AIT-621: the sandbox Page's m.me handle (or numeric id). Empty = the
// Messenger tab is not offered on this environment.
const SANDBOX_FB_PAGE_USERNAME = import.meta.env.VITE_SANDBOX_FB_PAGE_USERNAME ?? '';

// ---------------------------------------------------------------------------
// StageIndicator — two-stage progress rendered at the top of the page.
// Labels sit under the dots; the connector between dots colorizes up to
// the current active/complete step.
// ---------------------------------------------------------------------------

type StageState = 'upcoming' | 'active' | 'complete';

function StageIndicator({ step1Complete }: { step1Complete: boolean }) {
  const states: [StageState, StageState] = step1Complete
    ? ['complete', 'active']
    : ['active', 'upcoming'];

  return (
    <ol
      aria-label="Quick Start progress"
      className="flex items-start gap-2"
    >
      <StageItem index={1} label="Send us the code" state={states[0]} />
      <span
        aria-hidden="true"
        className={`mt-4 h-px flex-1 ${
          states[0] === 'complete' ? 'bg-primary' : 'bg-border'
        }`}
      />
      <StageItem
        index={2}
        label="Copy and paste instructions"
        state={states[1]}
      />
    </ol>
  );
}

function StageItem({
  index,
  label,
  state,
}: {
  index: 1 | 2;
  label: string;
  state: StageState;
}) {
  const dotClasses =
    state === 'complete'
      ? 'bg-primary text-primary-foreground ring-4 ring-primary/10'
      : state === 'active'
        ? 'bg-background text-primary border-2 border-primary ring-4 ring-primary/10'
        : 'bg-background text-muted-foreground border border-border';
  const labelClasses =
    state === 'upcoming'
      ? 'text-muted-foreground'
      : 'text-foreground font-medium';

  return (
    <li className="flex w-24 flex-col items-center gap-2 text-center">
      <span
        aria-hidden="true"
        className={`inline-flex size-8 items-center justify-center rounded-full text-xs font-semibold tabular-nums ${dotClasses}`}
      >
        {state === 'complete' ? (
          <Check className="size-4" strokeWidth={3} />
        ) : (
          index
        )}
      </span>
      <span className={`text-xs leading-snug ${labelClasses}`}>{label}</span>
    </li>
  );
}

// ---------------------------------------------------------------------------
// BindBody — the QR / code / phone / send-button surface rendered inside
// Step 1. Transient refetch errors don't surface to the user; the background
// poll will pick the bind code up again on the next tick.
// ---------------------------------------------------------------------------

function BindBody({
  bindCode,
  workspaceId,
}: {
  bindCode: string | null;
  workspaceId: string | undefined;
}) {
  const instagramEnabled = useInstagramEnabled();
  const facebookEnabled = useFacebookEnabled() && SANDBOX_FB_PAGE_USERNAME !== '';
  const [channel, setChannel] = useState<'whatsapp' | 'instagram' | 'facebook'>('whatsapp');
  const waUrl = bindCode
    ? `https://wa.me/${SANDBOX_NUMBER}?text=${encodeURIComponent(bindCode)}`
    : '';
  // ig.me does not support text-prefill via ?text=, so the user pastes the
  // code manually after the DM opens — that's why we still surface the Copy
  // affordance prominently in the IG variant. m.me is the same: no prefill.
  const igUrl = bindCode ? `https://ig.me/m/${SANDBOX_IG_USERNAME}` : '';
  const fbUrl = bindCode ? `https://m.me/${SANDBOX_FB_PAGE_USERNAME}` : '';
  const [codeCopied, setCodeCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);

  const isWhatsapp = channel === 'whatsapp';
  const isFacebook = channel === 'facebook';
  const channelUrl = isWhatsapp ? waUrl : isFacebook ? fbUrl : igUrl;

  function handleOpenChannel() {
    if (!channelUrl) return;
    // Phase 125-06 — emit BEFORE window.open so the new tab can't steal
    // focus synchronously and race a post-open capture flush.
    if (workspaceId) {
      emit(
        isWhatsapp
          ? 'quickstart_whatsapp_link_opened'
          : isFacebook
            ? 'quickstart_facebook_link_opened'
            : 'quickstart_instagram_link_opened',
        { workspace_id: workspaceId },
      );
    }
    window.open(channelUrl, '_blank');
  }

  async function handleCopyCode() {
    if (!bindCode) return;
    await navigator.clipboard.writeText(bindCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 1800);
    if (workspaceId) {
      emit('quickstart_code_copied', { workspace_id: workspaceId });
    }
  }

  async function handleCopyPhone() {
    await navigator.clipboard.writeText(SANDBOX_NUMBER);
    setPhoneCopied(true);
    setTimeout(() => setPhoneCopied(false), 1800);
    if (workspaceId) {
      emit('quickstart_phone_copied', { workspace_id: workspaceId });
    }
  }

  const ctaDisabled = !bindCode;
  const destinationLabel = isWhatsapp
    ? 'Our WhatsApp number'
    : isFacebook
      ? 'Our Facebook Page'
      : 'Our Instagram account';
  const ctaLabel = isWhatsapp ? 'Send In WhatsApp' : isFacebook ? 'Send In Messenger' : 'Send In Instagram';
  const headline = isWhatsapp
    ? 'Send Us This Code via WhatsApp'
    : isFacebook
      ? 'Send Us This Code via Messenger'
      : 'Send Us This Code via Instagram';
  const qrLabel = isWhatsapp
    ? 'QR code linking to WhatsApp with your bind code pre-filled'
    : isFacebook
      ? 'QR code linking to Messenger with the sandbox Page'
      : 'QR code linking to Instagram DM with the sandbox account';
  const qrUrl = channelUrl;

  return (
    <div className="space-y-6">
      {(instagramEnabled || facebookEnabled) && (
        <Tabs
          value={channel}
          onValueChange={(v) => setChannel(v as 'whatsapp' | 'instagram' | 'facebook')}
        >
          <TabsList>
            <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
            {instagramEnabled && <TabsTrigger value="instagram">Instagram</TabsTrigger>}
            {facebookEnabled && <TabsTrigger value="facebook">Facebook</TabsTrigger>}
          </TabsList>
        </Tabs>
      )}

      <div className="grid gap-6 md:grid-cols-[1fr_auto] md:items-start md:gap-8">
        <div className="space-y-5">
          <div className="space-y-1">
            <h2 className="font-heading text-xl font-semibold leading-snug tracking-tight">
              {headline}
            </h2>
            <h3 className="text-sm font-medium text-muted-foreground">
              Tap the button or scan the QR code
            </h3>
          </div>
          <dl className="space-y-3">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {destinationLabel}
              </dt>
              <dd className="mt-1 flex items-center gap-2">
                {isWhatsapp ? (
                  <>
                    <span className="text-base font-semibold tabular-nums text-foreground">
                      {formatSandboxPhone(SANDBOX_NUMBER)}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyPhone}
                      className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={phoneCopied ? 'Number copied' : 'Copy number'}
                      title={phoneCopied ? 'Copied' : 'Copy to clipboard'}
                    >
                      {phoneCopied ? (
                        <Check className="size-3.5" />
                      ) : (
                        <Copy className="size-3.5" />
                      )}
                    </button>
                  </>
                ) : (
                  <span className="text-base font-semibold text-foreground">
                    {isFacebook ? `m.me/${SANDBOX_FB_PAGE_USERNAME}` : `@${SANDBOX_IG_USERNAME}`}
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Code
              </dt>
              <dd className="mt-1 flex items-center gap-2">
                <span className="font-mono text-base font-semibold tracking-[0.08em] text-foreground">
                  {bindCode ?? '…'}
                </span>
                {bindCode && (
                  <button
                    type="button"
                    onClick={handleCopyCode}
                    className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={codeCopied ? 'Code copied' : 'Copy code'}
                    title={codeCopied ? 'Copied' : 'Copy to clipboard'}
                  >
                    {codeCopied ? (
                      <Check className="size-3.5" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </button>
                )}
              </dd>
            </div>
          </dl>
        </div>

        <div className="flex flex-col items-center gap-3">
          {/* The caption slot is rendered for both channels so switching tabs
              doesn't reflow the QR position. The IG variant uses `invisible`
              so the text occupies layout space but stays unread by AT. */}
          <p
            aria-hidden={!isWhatsapp}
            className={`text-center text-xs text-muted-foreground ${
              isWhatsapp ? '' : 'invisible'
            }`}
          >
            Scan with your phone&apos;s camera, not WhatsApp.
          </p>
          <div className="rounded-md border bg-background p-2">
            {bindCode ? (
              <>
                <QRCode value={qrUrl} size={132} aria-label={qrLabel} />
                <span className="sr-only">Link: {qrUrl}</span>
              </>
            ) : (
              <div className="flex size-[132px] items-center justify-center">
                <Loader2 className="size-8 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <Button
            size="default"
            onClick={handleOpenChannel}
            disabled={ctaDisabled}
          >
            {ctaLabel}
          </Button>
        </div>
      </div>

      <div
        role="status"
        aria-live="polite"
        className="flex items-center justify-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2
          className="size-4 animate-spin text-primary"
          aria-hidden="true"
        />
        Waiting for your message…
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SessionPicker — dropdown rendered inside Step 2 when the workspace has
// two or more active sandbox sessions. Lets the user choose which session's
// instructions to generate.
// ---------------------------------------------------------------------------

// Display label for a sandbox session in the picker. WA → phone; IG →
// resolved @handle, with raw IGSID fallback while the username resolver
// hasn't populated yet, and a generic label when both are absent so the
// picker can never render a blank row.
function sessionPickerLabel(s: SandboxSession): string {
  if (s.type === 'instagram') {
    if (s.senderInstagramUsername) return `@${s.senderInstagramUsername}`;
    return s.senderInstagramId ?? 'Instagram session';
  }
  if (s.type === 'facebook') return s.facebookSenderName ?? 'Facebook session';
  return s.phone ?? '—';
}

function SessionPicker({
  activeSessions,
  selectedSessionId,
  onSelectSession,
}: {
  activeSessions: SandboxSession[];
  selectedSessionId: string | null;
  onSelectSession: (id: string) => void;
}) {
  return (
    <div className="space-y-3">
      <p
        id="quick-start-picker-help"
        className="text-sm text-muted-foreground"
      >
        Choose the testing session you want your AI to use.
      </p>
      <Select value={selectedSessionId ?? ''} onValueChange={(value) => { if (value) onSelectSession(value); }}>
        <SelectTrigger
          id="quick-start-picker"
          aria-label="Connected testing session"
          aria-describedby="quick-start-picker-help"
        >
          <SelectValue placeholder="Select a testing session">
            {(value: unknown) => {
              if (typeof value !== 'string') return 'Select a testing session';
              const match = activeSessions.find((s) => s.id === value);
              return match ? sessionPickerLabel(match) : 'Select a testing session';
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {activeSessions.map((s) => (
            <SelectItem key={s.id} value={s.id}>
              {sessionPickerLabel(s)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step1Card — bind surface, shown only when the workspace has zero active
// sandbox sessions. The moment a session lands (polling + focus refetch),
// the page swaps this for Step2Card with an entrance animation.
// ---------------------------------------------------------------------------

function Step1Card({
  bindCode,
  bindCodeError,
  onRetryBindCode,
  workspaceId,
  // Phase 129 Plan 06 — when the user enters the "Change →" escape hatch
  // from the consent banner, Step1Card renders an additional headline that
  // explicitly tells them this is a fresh bind for a different number. The
  // existing "Send Us This Code via WhatsApp" H2 inside BindBody stays as
  // the action affordance heading, so the existing role-by-name lookups in
  // quick-start.page.test.ts keep working unchanged.
  inChangeFlow = false,
}: {
  bindCode: string | null;
  bindCodeError: boolean;
  onRetryBindCode: () => void;
  workspaceId: string | undefined;
  inChangeFlow?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Step 1
        </p>
        {inChangeFlow && (
          <h2 className="font-heading text-lg font-semibold leading-snug">
            Connect Channel
          </h2>
        )}
      </CardHeader>
      <CardContent>
        {bindCodeError && !bindCode ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">
              Couldn&apos;t load your sandbox code.
            </p>
            <Button variant="outline" size="sm" onClick={onRetryBindCode}>
              Retry
            </Button>
          </div>
        ) : (
          <BindBody bindCode={bindCode} workspaceId={workspaceId} />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Step2Card — rendered once the workspace has at least one active sandbox
// session. When the workspace has multiple sessions, a SessionPicker lets
// the user choose which session's instructions to generate.
// ---------------------------------------------------------------------------

function Step2Card({
  activeSessions,
  selectedSession,
  selectedSessionId,
  onSelectSession,
  sandboxSessionPublicId,
  workspaceId,
}: {
  activeSessions: SandboxSession[];
  selectedSession: SandboxSession | null;
  selectedSessionId: string | null;
  onSelectSession: (id: string) => void;
  sandboxSessionPublicId?: string;
  workspaceId: string | undefined;
}) {
  const showPicker = activeSessions.length >= 2;
  // Picker covers the mixed-channel case; when only one session is bound, the
  // description can name its channel directly. Mixed-channel just says
  // "messages" to stay accurate without picking a side.
  const channels = new Set(
    activeSessions.map((s) => s.type ?? 'whatsapp'),
  );
  const nounFor = (type: string) =>
    type === 'instagram' ? 'Instagram' : type === 'facebook' ? 'Facebook' : 'WhatsApp';
  const channelNoun =
    channels.size === 1 ? nounFor([...channels][0]) : nounFor(selectedSession?.type ?? 'whatsapp');
  return (
    <Card className="animate-in fade-in-0 slide-in-from-bottom-4 duration-500 ease-out">
      <CardHeader>
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Step 2
        </p>
        <h2 className="font-heading text-lg leading-snug font-semibold">
          Build With AI
        </h2>
        <CardDescription>
          This action will make your AI clone our open-source starter project
          from GitHub, fill in your credentials, and run it locally so you can
          send and receive {channelNoun} messages in seconds.
        </CardDescription>
      </CardHeader>
      {showPicker && (
        <CardContent>
          <SessionPicker
            activeSessions={activeSessions}
            selectedSessionId={selectedSessionId}
            onSelectSession={(id) => {
              onSelectSession(id);
              // Phase 125-06 — emit AFTER the parent handler runs so state
              // reflects the new selection before the capture settles.
              if (workspaceId) {
                emit('quickstart_session_selected', {
                  workspace_id: workspaceId,
                  session_public_id: id,
                });
              }
            }}
          />
        </CardContent>
      )}
      <CardFooter>
        <BootstrapCodeDialog
          trigger={<Button>Copy Instructions For Your AI</Button>}
          sandboxSessionPublicId={sandboxSessionPublicId}
          workspaceId={workspaceId}
        />
      </CardFooter>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// QuickStartPage (exported) — main route component for /w/:ws/sandbox/quick-start
// ---------------------------------------------------------------------------

// Map the all-optional wire shape (SandboxSessionDTO) to the all-required app
// shape (SandboxSession) by reading each field explicitly. The DTO is NOT
// structurally assignable to SandboxSession — required app fields
// (workspaceId, createdByUserId, accessToken, hmacSecret, createdAt,
// updatedAt) are optional on the wire — so a blanket `as` would hide a
// genuinely missing field. The backend always populates these for active
// sessions; the fallbacks only satisfy the type system for edge payloads.
// `publicId` is app-local (absent from the wire DTO) and stays undefined.
function toSandboxSession(dto: SandboxSessionDTO): SandboxSession {
  return {
    id: dto.id,
    workspaceId: dto.workspaceId ?? '',
    createdByUserId: dto.createdByUserId ?? '',
    phone: dto.phone,
    accessToken: dto.accessToken ?? '',
    status: dto.status,
    webhookUrl: dto.webhookUrl ?? null,
    cloudflareTunnelId: dto.cloudflareTunnelId ?? null,
    hostname: dto.hostname ?? null,
    hmacSecret: dto.hmacSecret ?? '',
    activatedAt: dto.activatedAt ?? null,
    createdAt: dto.createdAt ?? '',
    updatedAt: dto.updatedAt ?? '',
    origin: dto.origin,
    type: dto.type,
    facebookSenderName: dto.facebookSenderName ?? null,
    senderInstagramId: dto.senderInstagramId,
    accountInstagramId: dto.accountInstagramId,
    senderInstagramUsername: dto.senderInstagramUsername,
  };
}

export function QuickStartPage() {
  const fetchWithAuth = useFetch();

  // Demo-handoff landing done — clear the one-shot flag RootRedirect used to
  // send the user here, so later logins in this tab land normally.
  useEffect(() => {
    clearHandoffRedirectPending();
  }, []);

  // Phase 125-06 — workspace publicId for PostHog emit baseline (CONTEXT §16).
  // Optional because the context MAY be momentarily null during authenticated
  // app boot; analytics.ts emit() no-ops when workspaceId is undefined.
  const { activeWorkspace, sessionScoped } = useWorkspace();
  const workspaceId = activeWorkspace?.id;

  // Phase 130 SSE — replaces the /sandbox/sessions poll + focus/visibility
  // refetch + 3-second setInterval. The hook opens one persistent
  // EventSource('/sandbox/sessions/stream') and applies snapshot/upsert/delete
  // frames to local state. Zero polling calls remain after this change.
  const {
    sessions: streamSessions,
    loading: sessionsLoading,
    applySnapshot,
    removeSession,
  } = useSandboxSessionsStream();
  const sessions = streamSessions.map(toSandboxSession);

  // Bind code via TanStack Query — fetched once on mount (no focus refetch:
  // codes are stable per (user, workspace) until consumed by an arriving
  // WhatsApp message). PHONE_ALREADY_BOUND cannot be emitted by this endpoint
  // (it's only thrown by the forwarder at message-consume time, with a reply
  // sent directly to WhatsApp), so no special-case handling is needed here.
  // The error state is only surfaced when there's no code to show; the Retry
  // button refetches.
  const bindCodeQuery = useBindCode(workspaceId, sessionScoped);
  const bindCode = bindCodeQuery.data?.code ?? null;
  const bindCodeError = bindCodeQuery.isError;

  // User's explicit picker selection (only set via onSelectSession). When
  // null, the component derives the selection from activeSessions (most
  // recently bound). Keeping this minimal avoids the
  // react-hooks/set-state-in-effect lint error that a useEffect-driven
  // default-selection would trigger.
  const [userSelectedSessionId, setUserSelectedSessionId] = useState<
    string | null
  >(null);

  const activeSessions = useMemo(
    () => sessions.filter((s) => s.status === 'active'),
    [sessions],
  );

  // Derive effective selection in render (not in effect). Prefer the user's
  // explicit pick if still valid; otherwise fall back to the most-recently
  // bound active session.
  const effectiveSelectedSessionId: string | null = useMemo(() => {
    if (activeSessions.length === 0) return null;
    if (
      userSelectedSessionId !== null &&
      activeSessions.some((s) => s.id === userSelectedSessionId)
    ) {
      return userSelectedSessionId;
    }
    const mostRecent = [...activeSessions].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
    return mostRecent.id;
  }, [activeSessions, userSelectedSessionId]);

  // Focus/visibility safety net catches dropped SSE BOUND events. The 3s
  // setInterval poll the user complained about is gone — this is a one-shot
  // per tab return.
  useEffect(() => {
    const safetyRefetch = async () => {
      try {
        if (!sessionScoped) return;
        const res = await fetchWithAuth('/sandbox/sessions');
        if (!res.ok) return;
        const data = (await res.json()) as SandboxSessionDTO[];
        applySnapshot(data);
      } catch {
        // best-effort; SSE auto-reconnect will eventually deliver state.
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void safetyRefetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', safetyRefetch);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', safetyRefetch);
    };
  }, [fetchWithAuth, applySnapshot, sessionScoped]);

  const selectedSession =
    activeSessions.find((s) => s.id === effectiveSelectedSessionId) ?? null;

  // Phase 129 Plan 06 — change-flow escape hatch. When the URL carries
  // `?change=1` we force Step1Card render even if the workspace already has
  // an active session, so the user can bind a different phone (the auto-
  // connected demo session lingers until the new bind succeeds).
  const {
    isChangeFlow,
    demoSession,
    enterChangeFlow,
    exitChangeFlow,
    dismissDemoSession,
  } = useChangeFlow(activeSessions);

  const step2Enabled = selectedSession !== null && !isChangeFlow;

  // Phase 129 Plan 06 — when the user is in the change flow AND a NEW
  // (non-demo) active session lands, fire the DELETE on the demo session
  // and exit the change flow. Abandoning the change flow (navigating away
  // or removing `?change=1` without binding) intentionally does NOT fire
  // the DELETE — the gate here is the appearance of a non-demo active
  // session.
  useEffect(() => {
    if (!isChangeFlow) return;
    if (!demoSession) return;
    const nonDemoActive = activeSessions.filter(
      (s) => (s.publicId ?? s.id) !== (demoSession.publicId ?? demoSession.id),
    );
    if (nonDemoActive.length === 0) return;
    let cancelled = false;
    void (async () => {
      try {
        await dismissDemoSession();
        // Optimistically drop the demo session from local state. The
        // backend's deleted-event publish is best-effort, so relying on
        // SSE alone risks the consent banner reappearing if the publish
        // is dropped. mirrors sandbox.tsx's DeleteConfirmDialog onDeleted.
        if (!cancelled) {
          removeSession(demoSession.publicId ?? demoSession.id);
        }
      } finally {
        if (!cancelled) {
          exitChangeFlow();
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // demoSession identity is derived from activeSessions inside the hook;
    // including activeSessions is sufficient. dismissDemoSession and
    // exitChangeFlow are stable callbacks.
  }, [
    isChangeFlow,
    demoSession,
    activeSessions,
    dismissDemoSession,
    exitChangeFlow,
    removeSession,
  ]);

  // Wait for the first SSE snapshot before deciding Step 1 vs Step 2.
  // streamSessions starts as [] on every mount, so without this gate a
  // workspace that already has active sessions briefly renders the bind
  // flow with a fresh code before snapshot reseeds — the user could
  // click into the wrong flow during that flash.
  if (sessionsLoading) {
    return (
      <div className="mx-auto flex max-w-4xl items-center justify-center pb-16">
        <Loader2
          className="size-6 animate-spin text-muted-foreground"
          aria-label="Loading sandbox sessions"
        />
      </div>
    );
  }

  return (
    <PageContainer variant="scroll">
      <header className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Quick Start</h1>
      </header>

      <div className="mb-8">
        <StageIndicator step1Complete={step2Enabled} />
      </div>

      <div className="space-y-8">
        {/* Phase 129 Plan 06 — consent banner. Renders ABOVE the active card
            when a demo-handoff session exists AND we're NOT in the change
            flow (clicking Change → flips us into change flow, where the user
            is binding a different number and the banner would be misleading). */}
        {/* Banner is only useful for the freshly-onboarded single-session
            case (user just signed up from the homepage demo and lands here
            with exactly one auto-connected session). Once the workspace has
            multiple sessions, the user already knows where to connect, so
            the banner becomes noise — hide it. */}
        {demoSession &&
          !isChangeFlow &&
          workspaceId &&
          activeSessions.length === 1 && (
            <DemoHandoffBanner
              workspaceId={workspaceId}
              sessionPublicId={demoSession.publicId ?? demoSession.id}
              phone={demoSession.phone ?? ''}
              // The homepage demo hands off WhatsApp or Instagram sessions only.
              senderType={demoSession.type === 'instagram' ? 'instagram' : 'whatsapp'}
              senderInstagramUsername={demoSession.senderInstagramUsername}
              onChangeClick={enterChangeFlow}
            />
          )}

        {step2Enabled ? (
          <Step2Card
            activeSessions={activeSessions}
            selectedSession={selectedSession}
            selectedSessionId={effectiveSelectedSessionId}
            onSelectSession={setUserSelectedSessionId}
            sandboxSessionPublicId={selectedSession?.id}
            workspaceId={workspaceId}
          />
        ) : (
          <Step1Card
            bindCode={bindCode}
            bindCodeError={bindCodeError}
            onRetryBindCode={() => void bindCodeQuery.refetch()}
            workspaceId={workspaceId}
            inChangeFlow={isChangeFlow}
          />
        )}

        {/* Phase 129 Plan 06 — invisible test hook used by the Wave-0
            Playwright case `quick-start-handoff-banner`. Gated on
            `VITE_E2E_TEST_HOOKS` (set only by the Playwright webServer env
            block in frontend/playwright.config.ts) so the hook lives in the
            CI-faithful production build the pre-push runs against, but stays
            absent from real staging/prod bundles where the env var is unset.
            Vite tree-shakes the entire branch when the variable is missing.

            The button has `display: none` so the test hook is invisible to a
            real user; the test invokes it via `dispatchEvent('click')` on the
            test id.

            Also gated to `isChangeFlow && demoSession` — outside the change
            flow there's nothing to dismiss, and surfacing the test-id
            unconditionally would let a stale Playwright handle resolve
            against the wrong page state. */}
        {import.meta.env.VITE_E2E_TEST_HOOKS === '1' &&
          isChangeFlow &&
          demoSession && (
            <button
              type="button"
              data-testid="alternate-step1-complete"
              style={{ display: 'none' }}
              onClick={() => {
                void dismissDemoSession();
              }}
            >
              alternate step-1 complete (test hook)
            </button>
          )}
      </div>
    </PageContainer>
  );
}
